"use client";

import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { getCartIdByLocale, hasArrayElements } from "@/apiUtils/commonUtils";
import { analyticBuyFlows, analyticEventTypes, SaleorAttributes } from "@/apiUtils/constants";
import { getFromCookieMetadataSA } from "@/apiUtils/cookieDataUtilsServerActions";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import {
  CheckoutResponse,
  LCSkuType,
  MediaItem,
  Product,
  SaleorMetadata,
  TranslationData,
  VariantItemPdp,
  PairEyewearTopsContent,
} from "@/common/lib/types";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useGlobalStore } from "@/store/store";
import { getLangFromLocale } from "@/utils/common";
import { generateProductInfo } from "@/utils/lensConfigurator";
import { FC, useMemo } from "react";
import Modal from "../ui/modal_new/Modal";
import AddToCartHOC from "./AddToCartHoc";
import LensConfiguratorProvider from "./context/Provider";
import LensConfigurator, { LocalAddToCartFuncResponse } from "./LensConfigurator";
import { getAttributeValue } from "@/apiUtils/productUtils";

export type LCPProductInfo = ReturnType<typeof generateProductInfo>;

interface ILensConfiguratorModalProps {
  isOpen: boolean;
  onCloseLensConfigurator: () => void;
  toastToggle: (active: boolean) => void;
  locale: string;
  handleAddToCart?: (
    cartId: string,
    id: string,
    quantity: number,
    locale: string,
    metadata?: SaleorMetadata[],
    addOns?: {
      variantId: string;
      quantity: number;
      parent?: string;
      metadata?: Record<string, any>;
    }[],
    prescription?: string,
    magrabiId?: string,
    queryID?: string,
  ) => Promise<LocalAddToCartFuncResponse>;
  product: Product;
  variant?: VariantItemPdp;
  translations: TranslationData;
  source?: string;
  sourceId?: string;
  hitIndex?: number;
  queryID?: string;
  isFetchingPrescriptions: boolean;
  lensConfiguratorImageURL: string;
  pairEyewearTopsContent?: PairEyewearTopsContent | null;
}

const LCPModal: FC<ILensConfiguratorModalProps> = ({
  isOpen,
  locale,
  product,
  variant,
  source,
  sourceId,
  hitIndex,
  queryID,
  toastToggle,
  translations,
  handleAddToCart,
  onCloseLensConfigurator,
  isFetchingPrescriptions,
  lensConfiguratorImageURL,
  pairEyewearTopsContent,
}) => {
  const { showToast } = useCustomToast();
  const lang = getLangFromLocale(locale);
  const selectedParentBrand = useMemo(
    () =>
      (getAttributeValue(
        product?.attributes ?? [],
        SaleorAttributes.PARENT_BRAND,
        null,
        lang,
      ) ?? "") as string,
    [product, lang],
  );
  const isPairEyewear = selectedParentBrand === "pair_eyewear";

  const { updateCartCount, cartCount, user, hasPrescriptions } = useGlobalStore();
  const isValidUser = useMemo(() => !!user?.email && hasPrescriptions, [user, hasPrescriptions]);

  const SelectedVariantName = useMemo(() => {
    const language = getLangFromLocale(locale as string);
    const VariantTitle = variant?.attributes?.find((item) => item?.attribute?.slug === "title");
    const envVariable = process.env.NEXT_PUBLIC_BRAND_ID ?? process.env.NEXT_PUBLIC_FALLBACK_BRAND_ID ?? "magrabi"
    if (language === "ar") {
      const ArabicVal = VariantTitle?.values[0].translation?.scoppable;
      const jsonObject = JSON?.parse(ArabicVal || "");
      return jsonObject?.[envVariable]
    } else {
      const EnglishVal = VariantTitle?.values[0]?.scoppable;
      const jsonObject = JSON?.parse(EnglishVal || "");
      return jsonObject?.[envVariable]
    }
  }, [product, variant?.attributes]);

  console.log("SelectedVariantName", SelectedVariantName);

  const handleLocalAddToCart = async (
    id: string,
    addOnSkus?: LCSkuType,
    prescription?: string,
    metadata?: SaleorMetadata[],
  ): Promise<LocalAddToCartFuncResponse> => {
    startPageTransition(false);
    const cartId: string = await getFromCookieMetadataSA(getCartIdByLocale(locale));

    const addOns: {
      variantId: string;
      quantity: number;
      parent?: string;
      metadata?: SaleorMetadata[];
    }[] = [
        ...(addOnSkus?.left?.id
          ? [{ variantId: addOnSkus.left.id, quantity: 1, metadata: addOnSkus.left?.metadata || [] }]
          : []),
        ...(addOnSkus?.right?.id
          ? [
            {
              variantId: addOnSkus.right.id,
              quantity: 1,
              metadata: addOnSkus.right?.metadata || [],
            },
          ]
          : []),
      ];

    const response = await handleAddToCart?.(
      cartId,
      id || variant?.id || "",
      1,
      locale,
      metadata,
      addOns,
      prescription,
      user?.magrabi_id || "",
      queryID,
    );

    if (!response || hasArrayElements(response.response?.errors)) {
      return { cartCount: 0, response: (response ?? {}) as CheckoutResponse, success: false };
    }

    if (response?.response?.checkout) {
      await handlePostAnalytics({
        eventType: analyticEventTypes.ACTIONS,
        data: {
          target: analyticEvents.ADD_TO_CART,
          product: {
            ...product, hitIndex, selectedQuantity: 1, addOnSkus,
            name: SelectedVariantName,
          },
          selectedVariantId: id ?? variant?.id,
          source,
          sourceId,
          buyFlow: analyticBuyFlows.STANDARD_FLOW,
        },
      });
    }
    if (!isPairEyewear) {
      onCloseLensConfigurator();
    }
    toastToggle(true);
    updateCartCount(response?.cartCount ?? cartCount);
    endPageTransition();
    setTimeout(() => toastToggle(false), 2500);

    showToast({
      redirectUrl: "/cart",
      imgUrl: lensConfiguratorImageURL,
      text: translations?.common?.common_added_to_cart_toast_message ?? "Added to cart",
    });
    return response as LocalAddToCartFuncResponse;
  };

  const language = useMemo(() => getLangFromLocale(locale), [locale]);
  const productInfo = useMemo(
    () => {
      const info = generateProductInfo(product, variant, language);
      info.pairEyewearTopsContent = pairEyewearTopsContent;
      return info;
    },
    [product, variant, language, pairEyewearTopsContent],
  );

  if (!productInfo.image || !productInfo.image.url) {
    productInfo.image = { url: lensConfiguratorImageURL } as MediaItem;
    // const values = variant?.attributes.find((a) => a.attribute.slug === "product_image");
    // const image = values?.values?.at?.(0)?.name;

    // try {
    //   const imageURL = image?.split(",")?.[2]?.split(" ")?.[2]?.substring(1);
    //   productInfo.image = {} as MediaItem;
    //   productInfo.image.url = imageURL ?? "";
    // } catch (error) {
    //   console.log(error);
    // }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseLensConfigurator}
      slots={{
        close: { visible: false },
        root: { className: "px-0 no-scrollbar isolate z-[20]" },
        container: { className: "w-full overflow-y-scroll no-scrollbar h-screen " },
      }}
    >
      <LensConfiguratorProvider initialStep={{ productInfo, excludePrescription: !isValidUser }}>
        <LensConfigurator
          locale={locale}
          translations={translations}
          handleAddToCart={handleLocalAddToCart}
          onCloseLensConfigurator={onCloseLensConfigurator}
          isFetchingPrescriptions={isFetchingPrescriptions}
        />
      </LensConfiguratorProvider>
    </Modal>
  );
};

export default AddToCartHOC(LCPModal);
